import Logo from '@/components/layout/Logo'
import { useUnreadMessages } from '@/contexts/MessagesContext'
import { useAuth } from '@/hooks/useAuth'
import { useProfile } from '@/hooks/useProfile'
import { buildSearchPath } from '@/lib/search'
import { getStorePath } from '@/lib/storeSlug'
import { lumi } from '@/lib/lumi'
import {Compass, LayoutDashboard, LogOut, Mail, Menu, Radio, Search, Sparkles, Store, X} from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

interface HeaderProps {
  onAuthOpen: () => void
}

function Header({ onAuthOpen }: HeaderProps) {
  const { user, isAuthenticated } = useAuth()
  const { profile } = useProfile(user?.userId)
  const { unreadCount } = useUnreadMessages()
  const location = useLocation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const unreadLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  useEffect(() => {
    setMenuOpen(false)
    setSearchOpen(false)
  }, [location.pathname, location.search])

  const storePath = getStorePath(profile || { userId: user?.userId })

  const handleSignOut = async () => {
    await lumi.auth.signOut()
    setMenuOpen(false)
    navigate('/')
  }

  const handleSearch = (event: FormEvent) => {
    event.preventDefault()
    navigate(buildSearchPath({ query }))
    setSearchOpen(false)
  }

  const menuItem = (to: string, label: string, Icon: typeof Compass) => {
    const active = location.pathname === to
    return (
      <Link
        to={to}
        onClick={() => setMenuOpen(false)}
        className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-extrabold transition-colors ${
          active
            ? 'bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800]'
            : 'text-white hover:bg-white/10'
        }`}
      >
        <Icon className="h-4 w-4" />
        {label}
      </Link>
    )
  }

  return (
    <header className="sticky top-0 z-40 border-b-4 border-[#FFDE00]/80 bg-black/80 backdrop-blur-xl">
      <div className="relative mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
        <button
          type="button"
          onClick={() => {
            setMenuOpen(open => !open)
            setSearchOpen(false)
          }}
          className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-white/15 text-white transition-colors hover:border-white/40 hover:bg-white/10"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>

        <Link to="/" className="absolute left-1/2 -translate-x-1/2 transition-transform hover:-rotate-2">
          <Logo />
        </Link>

        <div className="flex items-center gap-1">
          {isAuthenticated ? (
            <Link
              to="/messages"
              className="relative flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
              aria-label={unreadCount > 0 ? `Messages, ${unreadCount} unread` : 'Messages'}
            >
              <Mail className="h-6 w-6 text-white" strokeWidth={2.4} />
              {unreadCount > 0 ? (
                <span className="absolute right-0.5 top-0.5 min-w-[18px] rounded-full bg-[#FF1A1A] px-1 text-center text-[10px] font-extrabold leading-[18px] text-white">
                  {unreadLabel}
                </span>
              ) : null}
            </Link>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setSearchOpen(open => !open)
              setMenuOpen(false)
            }}
            className="flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10"
            aria-label="Search cards and stores"
          >
            <Search className="h-6 w-6 text-white" strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {searchOpen ? (
        <div className="border-t border-white/10 bg-black/95 px-4 py-3 sm:px-6">
          <form onSubmit={handleSearch} className="mx-auto flex max-w-6xl gap-2">
            <input
              autoFocus
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search cards, prices, or stores"
              className="w-full rounded-full border-2 border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white outline-none placeholder:text-white/40 focus:border-[#FFDE00]"
            />
            <button
              type="submit"
              className="rounded-full bg-white px-4 py-2 text-sm font-extrabold text-black"
            >
              Go
            </button>
          </form>
        </div>
      ) : null}

      {menuOpen ? (
        <div className="absolute inset-x-0 top-full z-50 border-b-4 border-[#FFDE00]/80 bg-black/95 px-4 py-4 shadow-2xl sm:px-6">
          <div className="mx-auto flex max-w-md flex-col gap-1">
            {menuItem('/', 'Home', Compass)}
            {menuItem('/discover', 'Discover', Store)}
            {menuItem('/feed', 'Feed', Radio)}
            {menuItem('/search', 'Search', Search)}
            {isAuthenticated ? menuItem('/dashboard', 'My Profile', LayoutDashboard) : null}
            {isAuthenticated ? (
              <Link
                to="/messages"
                onClick={() => setMenuOpen(false)}
                className={`flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-extrabold transition-colors ${
                  location.pathname.startsWith('/messages')
                    ? 'bg-[#FFDE00] text-black shadow-[0_3px_0_#c9a800]'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <span className="flex items-center gap-3">
                  <Mail className="h-4 w-4" />
                  Messages
                </span>
                {unreadCount > 0 ? (
                  <span className="rounded-full bg-[#FF1A1A] px-2 py-0.5 text-[11px] font-extrabold text-white">
                    {unreadLabel}
                  </span>
                ) : null}
              </Link>
            ) : null}
            {isAuthenticated ? menuItem(storePath, 'Public store', Sparkles) : null}
            {menuItem('/pricing', 'Open beta', Sparkles)}
            {isAuthenticated ? (
              <button
                type="button"
                onClick={handleSignOut}
                className="mt-2 flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-extrabold text-white/75 hover:bg-white/10 hover:text-[#FF5A5A]"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false)
                  onAuthOpen()
                }}
                className="mt-2 rounded-full bg-[#FFDE00] px-4 py-3 text-sm font-extrabold text-black shadow-[0_4px_0_#c9a800]"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      ) : null}
    </header>
  )
}

export default Header
